/**
 * Escritor incremental para PDFs.
 * Permite adicionar modificações ao final de um PDF existente
 * sem reescrever todo o documento, preservando assinaturas digitais.
 */
import PDFCrossRefSection from "../document/PDFCrossRefSection";
import PDFTrailer from "../document/PDFTrailer";
import PDFTrailerDict from "../document/PDFTrailerDict";
import PDFName from "../objects/PDFName";
import PDFNumber from "../objects/PDFNumber";
import PDFObjectStream from "../structures/PDFObjectStream";
import CharCodes from "../syntax/CharCodes";
import { copyStringIntoBuffer, waitForTick } from "../../utils";
/**
 * Classe para gerenciar salvamentos incrementais de PDFs.
 */
class PDFIncrementalWriter {
    constructor(context, snapshot, objectsPerTick) {
        this.parsedObjects = 0;
        this.shouldWaitForTick = (n) => {
            this.parsedObjects += n;
            return this.parsedObjects % this.objectsPerTick === 0;
        };
        this.context = context;
        this.snapshot = snapshot;
        this.objectsPerTick = objectsPerTick;
    }
    /**
     * Serializa as mudanças incrementais para um buffer.
     */
    async serializeToBuffer() {
        const { size, indirectObjects, xref, trailerDict, trailer } = await this.computeBufferSize();
        // Cria o buffer final: original + incremental
        const totalSize = this.snapshot.originalSize + size;
        const buffer = new Uint8Array(totalSize);
        // Copia o documento original
        buffer.set(this.snapshot.originalBytes.slice(0, this.snapshot.originalSize));
        // Escreve a atualização incremental
        let offset = this.snapshot.originalSize;
        // Newlines antes dos objetos
        buffer[offset++] = CharCodes.Newline;
        buffer[offset++] = CharCodes.Newline;
        // Escreve os objetos modificados
        for (let idx = 0, len = indirectObjects.length; idx < len; idx++) {
            const [ref, object] = indirectObjects[idx];
            const objectNumber = String(ref.objectNumber);
            offset += copyStringIntoBuffer(objectNumber, buffer, offset);
            buffer[offset++] = CharCodes.Space;
            const generationNumber = String(ref.generationNumber);
            offset += copyStringIntoBuffer(generationNumber, buffer, offset);
            buffer[offset++] = CharCodes.Space;
            buffer[offset++] = CharCodes.o;
            buffer[offset++] = CharCodes.b;
            buffer[offset++] = CharCodes.j;
            buffer[offset++] = CharCodes.Newline;
            offset += object.copyBytesInto(buffer, offset);
            buffer[offset++] = CharCodes.Newline;
            buffer[offset++] = CharCodes.e;
            buffer[offset++] = CharCodes.n;
            buffer[offset++] = CharCodes.d;
            buffer[offset++] = CharCodes.o;
            buffer[offset++] = CharCodes.b;
            buffer[offset++] = CharCodes.j;
            buffer[offset++] = CharCodes.Newline;
            buffer[offset++] = CharCodes.Newline;
            const n = object instanceof PDFObjectStream ? object.getObjectsCount() : 1;
            if (this.shouldWaitForTick(n))
                await waitForTick();
        }
        // Escreve xref
        offset += xref.copyBytesInto(buffer, offset);
        buffer[offset++] = CharCodes.Newline;
        // Escreve trailer dict
        offset += trailerDict.copyBytesInto(buffer, offset);
        buffer[offset++] = CharCodes.Newline;
        buffer[offset++] = CharCodes.Newline;
        // Escreve trailer
        offset += trailer.copyBytesInto(buffer, offset);
        return buffer;
    }
    /**
     * Calcula o tamanho do buffer incremental.
     */
    async computeBufferSize() {
        // Offset inicial é o tamanho do documento original + 2 newlines
        let size = 2;
        const xref = PDFCrossRefSection.create();
        // Pega apenas os objetos modificados
        const allObjects = this.context.enumerateIndirectObjects();
        const modifiedObjects = [];
        for (const [ref, obj] of allObjects) {
            const refKey = `${ref.objectNumber}-${ref.generationNumber}`;
            if (this.snapshot.modifiedRefs.has(refKey)) {
                modifiedObjects.push([ref, obj]);
            }
        }
        // Calcula tamanhos e offsets
        for (let idx = 0, len = modifiedObjects.length; idx < len; idx++) {
            const indirectObject = modifiedObjects[idx];
            const [ref] = indirectObject;
            // Offset real = original size + incremental offset
            xref.addEntry(ref, this.snapshot.originalSize + size);
            size += this.computeIndirectObjectSize(indirectObject);
            if (this.shouldWaitForTick(1))
                await waitForTick();
        }
        const xrefOffset = this.snapshot.originalSize + size;
        size += xref.sizeInBytes() + 1; // '\n'
        const trailerDict = PDFTrailerDict.of(this.createTrailerDict());
        size += trailerDict.sizeInBytes() + 2; // '\n\n'
        const trailer = PDFTrailer.forLastCrossRefSectionOffset(xrefOffset);
        size += trailer.sizeInBytes();
        return {
            size,
            indirectObjects: modifiedObjects,
            xref,
            trailerDict,
            trailer,
        };
    }
    /**
     * Cria o dicionário trailer para atualização incremental.
     */
    createTrailerDict() {
        const dict = this.context.obj({
            Size: this.context.largestObjectNumber + 1,
            Root: this.context.trailerInfo.Root,
            Encrypt: this.context.trailerInfo.Encrypt,
            Info: this.context.trailerInfo.Info,
            ID: this.context.trailerInfo.ID,
        });
        // Adiciona referência ao xref anterior (Prev)
        dict.set(PDFName.of('Prev'), PDFNumber.of(this.snapshot.previousXRefOffset));
        return dict;
    }
    /**
     * Calcula o tamanho de um objeto indireto.
     */
    computeIndirectObjectSize([ref, object]) {
        const refSize = ref.sizeInBytes() + 3; // 'R' -> 'obj\n'
        const objectSize = object.sizeInBytes() + 9; // '\nendobj\n\n'
        return refSize + objectSize;
    }
}
PDFIncrementalWriter.forContext = (context, snapshot, objectsPerTick) => new PDFIncrementalWriter(context, snapshot, objectsPerTick);
export default PDFIncrementalWriter;
/**
 * Cria um snapshot do documento para salvamento incremental posterior.
 * @param bytes Os bytes originais do documento
 * @returns O snapshot do documento
 */
export function createDocumentSnapshot(bytes) {
    // Encontra o offset do último xref
    const previousXRefOffset = findLastXRefOffset(bytes);
    return {
        originalBytes: bytes,
        originalSize: bytes.length,
        previousXRefOffset,
        modifiedRefs: new Set(),
        timestamp: Date.now(),
    };
}
/**
 * Encontra o offset do último xref no documento.
 */
function findLastXRefOffset(bytes) {
    // Procura "startxref" de trás para frente
    const startxref = new TextEncoder().encode('startxref');
    let pos = bytes.length - 1;
    outer: while (pos >= startxref.length) {
        // Verifica se encontramos "startxref"
        let match = true;
        for (let i = 0; i < startxref.length; i++) {
            if (bytes[pos - startxref.length + 1 + i] !== startxref[i]) {
                match = false;
                break;
            }
        }
        if (match) {
            // Encontrou! Agora lê o número após startxref
            pos += 2; // Pula para depois de "startxref"
            // Pula whitespace
            while (pos < bytes.length &&
                (bytes[pos] === 0x20 ||
                    bytes[pos] === 0x0a ||
                    bytes[pos] === 0x0d ||
                    bytes[pos] === 0x09)) {
                pos++;
            }
            // Lê o número
            let numStr = '';
            while (pos < bytes.length && bytes[pos] >= 0x30 && bytes[pos] <= 0x39) {
                numStr += String.fromCharCode(bytes[pos]);
                pos++;
            }
            return parseInt(numStr, 10) || 0;
        }
        pos--;
    }
    return 0;
}
/**
 * Marca uma referência como modificada no snapshot.
 */
export function markRefAsModified(snapshot, ref) {
    const refKey = `${ref.objectNumber}-${ref.generationNumber}`;
    snapshot.modifiedRefs.add(refKey);
}
//# sourceMappingURL=PDFIncrementalWriter.js.map